import React, { useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { api, API } from "@/lib/api";
import { Upload, X, Loader2, ChevronLeft, ChevronRight, ImageIcon, Sparkles, Download, Send } from "lucide-react";
import { toast } from "sonner";

/**
 * Interactive apparel customizer.
 *
 * Props:
 *   product     — product object (uses .category to pick silhouette + pricing rules)
 *   placements  — current placements array (controlled)
 *   onChange    — (nextPlacements) => void
 *   brandColor  — CSS var name for accent (defaults to DTF blue)
 */
export default function ApparelCustomizer({ product, placements, onChange, brandColor = "var(--dtf-blue)" }) {
  const [view, setView] = useState("front"); // "front" | "back"
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [uploadingZoneId, setUploadingZoneId] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewEmail, setPreviewEmail] = useState("");
  const [previewName, setPreviewName] = useState("");
  const [previewNotes, setPreviewNotes] = useState("");
  const [previewSending, setPreviewSending] = useState(false);
  const fileInputRef = useRef(null);
  const silhouetteRef = useRef(null);

  const isZipUp = product.category === "zip-ups";
  const isTank = product.category === "tanks";
  const isHoodie = product.category === "hoodies";

  // Pricing constants (mirror backend logic — server is authoritative)
  const SMALL_PRICE = 3.0;
  const CENTER_BASE = isZipUp ? 8.0 : 6.0;
  const FREE_INCHES = 10.0;
  const UPCHARGE_PER_INCH = 1.0;

  // Zone geometry (percent of container). x,y = top-left, w,h = size.
  const FRONT_ZONES = [
    { id: "left-chest", label: "Left Chest", short: "L. CHEST", x: 30, y: 28, w: 13, h: 11, kind: "small" },
    { id: "right-chest", label: "Right Chest", short: "R. CHEST", x: 57, y: 28, w: 13, h: 11, kind: "small" },
    { id: "front-center", label: "Front Center", short: "FRONT CENTER", x: 32, y: 42, w: 36, h: 36, kind: "center" },
    ...(isTank
      ? []
      : [
          { id: "left-sleeve", label: "Left Sleeve", short: "L. SLV", x: 12, y: 24, w: 11, h: 10, kind: "small" },
          { id: "right-sleeve", label: "Right Sleeve", short: "R. SLV", x: 77, y: 24, w: 11, h: 10, kind: "small" },
        ]),
  ];
  const BACK_ZONES = [
    { id: "back-center", label: "Back Center", short: "BACK CENTER", x: 28, y: 24, w: 44, h: 52, kind: "center" },
  ];

  const zones = view === "front" ? FRONT_ZONES : BACK_ZONES;

  // Clip-path silhouette per garment (percent-based polygon)
  const silhouettePath = useMemo(() => {
    if (isTank) {
      return "polygon(38% 8%, 46% 2%, 54% 2%, 62% 8%, 74% 14%, 74% 100%, 26% 100%, 26% 14%)";
    }
    if (isHoodie || isZipUp) {
      // Rounded hood + shoulders + full torso + kangaroo (visual via inner div)
      return "polygon(32% 12%, 38% 4%, 46% 0%, 54% 0%, 62% 4%, 68% 12%, 98% 24%, 88% 38%, 76% 30%, 76% 100%, 24% 100%, 24% 30%, 12% 38%, 2% 24%)";
    }
    // Tee (default)
    return "polygon(30% 8%, 40% 2%, 60% 2%, 70% 8%, 98% 22%, 88% 36%, 76% 26%, 76% 100%, 24% 100%, 24% 26%, 12% 36%, 2% 22%)";
  }, [isTank, isHoodie, isZipUp]);

  const placementById = Object.fromEntries((placements || []).map((p) => [p.location, p]));

  const priceForZone = (zone, sizeInches) => {
    if (zone.kind === "small") return SMALL_PRICE;
    const size = sizeInches ?? FREE_INCHES;
    return +(CENTER_BASE + Math.max(0, size - FREE_INCHES) * UPCHARGE_PER_INCH).toFixed(2);
  };

  const upsertPlacement = (next) => {
    const others = (placements || []).filter((p) => p.location !== next.location);
    onChange([...others, next]);
  };

  const removePlacement = (zoneId) => {
    onChange((placements || []).filter((p) => p.location !== zoneId));
    if (selectedZoneId === zoneId) setSelectedZoneId(null);
  };

  const handleAddZone = (zone) => {
    setSelectedZoneId(zone.id);
    if (!placementById[zone.id]) {
      const p = {
        location: zone.id,
        ...(zone.kind === "center" ? { size_inches: FREE_INCHES } : {}),
      };
      upsertPlacement(p);
    }
  };

  const openFilePicker = (zoneId) => {
    setUploadingZoneId(zoneId);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset for re-upload of same file
    if (!file || !uploadingZoneId) return;
    const zoneId = uploadingZoneId;

    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large — max 25MB per design.");
      setUploadingZoneId(null);
      return;
    }

    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const existing = placementById[zoneId] || { location: zoneId };
      // Backend returns /api/uploads/xxx — prepend BACKEND_URL for browser display
      const absoluteUrl = data.url.startsWith("http") ? data.url : `${API.replace(/\/api$/, "")}${data.url}`;
      upsertPlacement({
        ...existing,
        artwork_url: data.url,
        artwork_preview_url: absoluteUrl,
        artwork_filename: file.name,
      });
      toast.success(`Design uploaded to ${zoneId.replace("-", " ")}.`);
    } catch (err) {
      console.error(err);
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploadingZoneId(null);
    }
  };

  const updateSize = (zoneId, size_inches) => {
    const existing = placementById[zoneId];
    if (!existing) return;
    upsertPlacement({ ...existing, size_inches });
  };

  const selectedZone = zones.find((z) => z.id === selectedZoneId) || null;
  const selectedPlacement = selectedZone ? placementById[selectedZone.id] : null;

  const totalCustomizationPerUnit = (placements || []).reduce((sum, pl) => {
    const zone = [...FRONT_ZONES, ...BACK_ZONES].find((z) => z.id === pl.location);
    if (!zone) return sum;
    return sum + priceForZone(zone, pl.size_inches);
  }, 0);

  // Capture the silhouette element as a PNG data URL (client-side, html2canvas)
  const captureMockup = async () => {
    if (!silhouetteRef.current) return null;
    // Momentarily hide the little X remove buttons for a clean mockup
    const removeBtns = silhouetteRef.current.querySelectorAll('[data-mockup-hide="1"]');
    removeBtns.forEach((el) => (el.style.visibility = "hidden"));
    try {
      const canvas = await html2canvas(silhouetteRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        logging: false,
      });
      return canvas.toDataURL("image/png");
    } finally {
      removeBtns.forEach((el) => (el.style.visibility = ""));
    }
  };

  const handleDownloadMockup = async () => {
    if ((placements || []).length === 0) {
      toast.error("Add at least one placement first.");
      return;
    }
    setDownloading(true);
    try {
      const dataUrl = await captureMockup();
      if (!dataUrl) throw new Error("Capture failed");
      const link = document.createElement("a");
      link.download = `${product.id}-mockup-${view}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Mockup downloaded.");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't create the mockup. Try again.");
    } finally {
      setDownloading(false);
    }
  };

  const handleSendPreview = async () => {
    if (!previewName.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    if (!previewEmail || !/^\S+@\S+\.\S+$/.test(previewEmail)) {
      toast.error("Enter a valid email so we can reply.");
      return;
    }
    if ((placements || []).length === 0) {
      toast.error("Add at least one placement first.");
      return;
    }
    setPreviewSending(true);
    try {
      const dataUrl = await captureMockup();
      if (!dataUrl) throw new Error("Capture failed");
      const cleanPlacements = (placements || []).map((p) => ({
        location: p.location,
        size_inches: p.size_inches,
        artwork_url: p.artwork_url,
      }));
      await api.post("/design-preview", {
        customer_email: previewEmail.trim(),
        customer_name: previewName.trim() || undefined,
        product_id: product.id,
        quantity: 1,
        notes: previewNotes.trim() || undefined,
        mockup_data_url: dataUrl,
        placements: cleanPlacements,
      });
      toast.success("Preview sent — we'll reply within one business day.");
      setPreviewOpen(false);
      setPreviewEmail("");
      setPreviewName("");
      setPreviewNotes("");
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send preview. Try again.");
    } finally {
      setPreviewSending(false);
    }
  };

  return (
    <div
      className="mt-10 border-2 border-black bg-white"
      style={{ boxShadow: `8px 8px 0 0 ${brandColor}` }}
      data-testid="apparel-customizer"
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml,application/pdf,image/gif"
        className="hidden"
        onChange={handleFileChange}
        data-testid="customizer-file-input"
      />

      {/* Header */}
      <div
        className="px-5 py-4 border-b-2 border-black flex items-center justify-between gap-3"
        style={{ background: "var(--dtf-yellow-soft, #fff4b8)" }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 border border-black inline-flex items-center justify-center"
            style={{ background: brandColor, color: "#fff" }}
          >
            <Sparkles size={16} />
          </div>
          <div>
            <p className="label-mono">DESIGN YOUR {isTank ? "TANK" : isHoodie ? "HOODIE" : isZipUp ? "ZIP-UP" : "TEE"}</p>
            <p className="font-mono text-[11px] text-[color:var(--muted-text)]">
              Click a zone to add your design. Each placement adds a fee.
            </p>
          </div>
        </div>
        <div className="hidden md:block text-right">
          <p className="label-mono text-[10px] text-[color:var(--muted-text)]">Custom fee / unit</p>
          <p
            className="font-mono text-xl font-bold"
            style={{ color: totalCustomizationPerUnit > 0 ? brandColor : "#111" }}
            data-testid="customizer-fee-per-unit"
          >
            +${totalCustomizationPerUnit.toFixed(2)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
        {/* --- Silhouette --- */}
        <div className="md:col-span-3 p-6 border-b-2 md:border-b-0 md:border-r-2 border-black relative">
          {/* Front / Back toggle */}
          <div className="flex items-center justify-center gap-0 mb-4">
            <button
              type="button"
              onClick={() => setView("front")}
              className="label-mono px-4 py-2 border border-black"
              style={
                view === "front"
                  ? { background: brandColor, color: "#fff" }
                  : { background: "#fff", color: "#111" }
              }
              data-testid="customizer-view-front"
            >
              <ChevronLeft size={12} className="inline mr-1" /> Front
            </button>
            <button
              type="button"
              onClick={() => setView("back")}
              className="label-mono px-4 py-2 border border-black border-l-0"
              style={
                view === "back"
                  ? { background: brandColor, color: "#fff" }
                  : { background: "#fff", color: "#111" }
              }
              data-testid="customizer-view-back"
            >
              Back <ChevronRight size={12} className="inline ml-1" />
            </button>
          </div>

          {/* Garment silhouette + zones */}
          <div ref={silhouetteRef} className="relative w-full max-w-[420px] mx-auto bg-white" style={{ aspectRatio: "3 / 4" }}>
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(160deg, #d9dde3 0%, #eef1f5 55%, #cfd4dc 100%)",
                clipPath: silhouettePath,
                WebkitClipPath: silhouettePath,
                border: "2px solid transparent",
              }}
              aria-hidden="true"
            />
            {/* Zip-up seam line */}
            {isZipUp && view === "front" && (
              <div
                className="absolute"
                style={{
                  left: "49.5%",
                  top: "12%",
                  width: "1%",
                  height: "88%",
                  background: "repeating-linear-gradient(to bottom, #666 0 4px, transparent 4px 8px)",
                }}
                aria-hidden="true"
              />
            )}
            {/* Hoodie pocket */}
            {(isHoodie || isZipUp) && view === "front" && (
              <div
                className="absolute border border-[color:#a9b0b8]"
                style={{
                  left: "30%",
                  top: "68%",
                  width: "40%",
                  height: "18%",
                  borderRadius: "6px",
                  background: "rgba(255,255,255,0.05)",
                }}
                aria-hidden="true"
              />
            )}

            {/* Zones */}
            {zones.map((zone) => {
              const placement = placementById[zone.id];
              const active = selectedZoneId === zone.id;
              const hasArt = !!placement?.artwork_preview_url;
              return (
                <button
                  key={zone.id}
                  type="button"
                  onClick={() => handleAddZone(zone)}
                  className="absolute group"
                  style={{
                    left: `${zone.x}%`,
                    top: `${zone.y}%`,
                    width: `${zone.w}%`,
                    height: `${zone.h}%`,
                    border: `2px dashed ${active ? brandColor : hasArt ? brandColor : "#111"}`,
                    background: hasArt ? "#fff" : "rgba(255,255,255,0.55)",
                    boxShadow: active ? `0 0 0 3px ${brandColor}` : "none",
                    transition: "box-shadow 120ms, background 120ms",
                  }}
                  data-testid={`customizer-zone-${zone.id}`}
                  title={zone.label}
                >
                  {hasArt ? (
                    <img
                      src={placement.artwork_preview_url}
                      alt={zone.label}
                      className="w-full h-full object-contain p-0.5"
                    />
                  ) : (
                    <span
                      className="label-mono flex items-center justify-center w-full h-full leading-[1] text-center overflow-hidden"
                      style={{ color: "#111", fontSize: zone.kind === "small" ? "7px" : "9px" }}
                    >
                      {zone.short || zone.label}
                    </span>
                  )}
                  {placement && (
                    <span
                      role="button"
                      data-mockup-hide="1"
                      onClick={(e) => {
                        e.stopPropagation();
                        removePlacement(zone.id);
                      }}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-black text-white inline-flex items-center justify-center border border-black cursor-pointer hover:bg-red-600"
                      data-testid={`customizer-zone-remove-${zone.id}`}
                      title="Remove"
                    >
                      <X size={10} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <p className="label-mono text-[10px] text-center text-[color:var(--muted-text)] mt-4">
            {view === "front"
              ? isTank
                ? "Front view · tap a zone (no sleeves on tanks)"
                : "Front view · tap any zone to add a design"
              : "Back view · tap the center zone to add a full-back print"}
          </p>
        </div>

        {/* --- Editor Panel --- */}
        <div className="md:col-span-2 p-5">
          {(placements || []).length === 0 && !selectedZone && (
            <div className="border border-dashed border-black p-4 text-center" data-testid="customizer-empty">
              <ImageIcon size={22} className="mx-auto text-[color:var(--muted-text)]" />
              <p className="font-mono text-sm font-bold mt-2">No placements yet.</p>
              <p className="label-mono text-[10px] text-[color:var(--muted-text)] mt-1">
                Tap a zone on the {view} view to start.
              </p>
              <p className="label-mono text-[10px] text-[color:var(--muted-text)] mt-3">
                Small logo · $3 · Chest / Sleeve
                <br />
                Center print · ${CENTER_BASE.toFixed(2)} · up to 10"
              </p>
            </div>
          )}

          {selectedZone && (
            <div className="border border-black p-4" data-testid="customizer-editor">
              <div className="flex items-center justify-between">
                <p className="label-mono">EDITING</p>
                <button
                  type="button"
                  onClick={() => setSelectedZoneId(null)}
                  className="label-mono hover:underline"
                  data-testid="customizer-close-editor"
                >
                  Done
                </button>
              </div>
              <h4 className="font-display text-2xl mt-1" style={{ color: brandColor }}>
                {selectedZone.label.toUpperCase()}
              </h4>

              {selectedZone.kind === "center" && (
                <div className="mt-4">
                  <label className="label-mono block mb-2">Design height (inches)</label>
                  <input
                    type="number"
                    min="4"
                    max="16"
                    step="0.5"
                    value={selectedPlacement?.size_inches ?? FREE_INCHES}
                    onChange={(e) => updateSize(selectedZone.id, parseFloat(e.target.value) || FREE_INCHES)}
                    className="brutal-input w-full"
                    data-testid={`customizer-size-input-${selectedZone.id}`}
                  />
                  <p className="label-mono text-[10px] text-[color:var(--muted-text)] mt-1">
                    First 10" included. +$1/inch above.
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={() => openFilePicker(selectedZone.id)}
                className="btn-brutal btn-brutal-outline mt-4 w-full justify-center"
                data-testid={`customizer-upload-btn-${selectedZone.id}`}
              >
                {uploadingZoneId === selectedZone.id ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={14} />
                    {selectedPlacement?.artwork_url ? "Replace Design" : "Upload Design"}
                  </>
                )}
              </button>

              {selectedPlacement?.artwork_filename && (
                <p className="label-mono text-[10px] text-[color:var(--muted-text)] mt-2 truncate">
                  {selectedPlacement.artwork_filename}
                </p>
              )}

              <div className="mt-4 pt-4 border-t border-black flex items-baseline justify-between">
                <span className="label-mono">Fee</span>
                <span className="font-mono text-lg font-bold" style={{ color: brandColor }}>
                  +${priceForZone(selectedZone, selectedPlacement?.size_inches).toFixed(2)}
                </span>
              </div>

              <button
                type="button"
                onClick={() => removePlacement(selectedZone.id)}
                className="label-mono w-full text-center text-[color:var(--muted-text)] hover:text-black mt-3"
                data-testid={`customizer-remove-btn-${selectedZone.id}`}
              >
                Remove this placement
              </button>
            </div>
          )}

          {/* Summary list */}
          {(placements || []).length > 0 && (
            <div className="mt-4">
              <p className="label-mono mb-2">PLACEMENTS · {(placements || []).length}</p>
              <ul className="space-y-2">
                {(placements || []).map((pl) => {
                  const zone = [...FRONT_ZONES, ...BACK_ZONES].find((z) => z.id === pl.location);
                  if (!zone) return null;
                  const price = priceForZone(zone, pl.size_inches);
                  return (
                    <li
                      key={pl.location}
                      className="flex items-center justify-between border border-black px-3 py-2"
                      data-testid={`customizer-list-${pl.location}`}
                    >
                      <div className="min-w-0">
                        <p className="font-mono text-sm font-bold truncate">{zone.label}</p>
                        <p className="label-mono text-[10px] text-[color:var(--muted-text)] truncate">
                          {pl.artwork_filename || "no file yet"}
                          {zone.kind === "center" && pl.size_inches ? ` · ${pl.size_inches}"` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm font-bold" style={{ color: brandColor }}>
                          +${price.toFixed(2)}
                        </span>
                        <button
                          type="button"
                          onClick={() => setSelectedZoneId(pl.location)}
                          className="label-mono underline hover:opacity-70"
                          data-testid={`customizer-list-edit-${pl.location}`}
                        >
                          Edit
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {product.customized_base_price != null && (placements || []).length > 0 && (
            <div className="mt-4 border border-black bg-[color:var(--muted)] p-3">
              <p className="label-mono text-[10px] text-[color:var(--muted-text)]">Base drops with any print</p>
              <p className="font-mono text-sm font-bold">
                <span className="line-through text-[color:var(--muted-text)]">${product.price.toFixed(2)}</span>{" "}
                → <span style={{ color: brandColor }}>${product.customized_base_price.toFixed(2)}</span>{" "}
                <span className="label-mono">/ EA + placements</span>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* --- Mockup actions --- */}
      {(placements || []).length > 0 && (
        <div className="border-t-2 border-black p-4 md:p-5 bg-[color:var(--muted)]" data-testid="customizer-mockup-bar">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="label-mono">MOCKUP</p>
              <p className="font-mono text-xs text-[color:var(--muted-text)]">
                Save a preview of your design or send it to us for approval before ordering.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleDownloadMockup}
                disabled={downloading}
                className="btn-brutal btn-brutal-outline"
                data-testid="customizer-download-mockup"
              >
                {downloading ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Rendering...
                  </>
                ) : (
                  <>
                    <Download size={14} /> Download PNG
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setPreviewOpen((v) => !v)}
                className="btn-brutal"
                style={{ background: brandColor, color: "#fff", borderColor: "#000" }}
                data-testid="customizer-open-preview-form"
              >
                <Send size={14} /> Send To Us For Approval
              </button>
            </div>
          </div>

          {previewOpen && (
            <div
              className="mt-4 border-2 border-black bg-white p-4"
              data-testid="customizer-preview-form"
            >
              <p className="label-mono mb-3">SEND FOR APPROVAL</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="label-mono block mb-1" htmlFor="preview-name">Your name *</label>
                  <input
                    id="preview-name"
                    type="text"
                    value={previewName}
                    onChange={(e) => setPreviewName(e.target.value)}
                    className="brutal-input w-full"
                    placeholder="Jane Doe"
                    required
                    data-testid="customizer-preview-name"
                  />
                </div>
                <div>
                  <label className="label-mono block mb-1" htmlFor="preview-email">Email *</label>
                  <input
                    id="preview-email"
                    type="email"
                    value={previewEmail}
                    onChange={(e) => setPreviewEmail(e.target.value)}
                    className="brutal-input w-full"
                    placeholder="you@example.com"
                    required
                    data-testid="customizer-preview-email"
                  />
                </div>
              </div>
              <div className="mt-3">
                <label className="label-mono block mb-1" htmlFor="preview-notes">Notes (optional)</label>
                <textarea
                  id="preview-notes"
                  rows={2}
                  value={previewNotes}
                  onChange={(e) => setPreviewNotes(e.target.value)}
                  className="brutal-input w-full"
                  placeholder="Anything we should know about the design or deadline?"
                  data-testid="customizer-preview-notes"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <button
                  type="button"
                  onClick={handleSendPreview}
                  disabled={previewSending}
                  className="btn-brutal"
                  style={{ background: brandColor, color: "#fff", borderColor: "#000" }}
                  data-testid="customizer-send-preview"
                >
                  {previewSending ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      <Send size={14} /> Send Preview
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewOpen(false)}
                  className="label-mono px-3 py-2 border border-black hover:bg-black hover:text-white transition-colors"
                  data-testid="customizer-cancel-preview"
                >
                  Cancel
                </button>
                <span className="label-mono text-[color:var(--muted-text)] text-[10px]">
                  We reply within 1 business day.
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
